import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import dayjs from "../../../utils/dayjs";
import { useMediaQuery } from "react-responsive";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  LayoutGrid,
  List,
  CalendarDays,
  ChevronDown,
  Sparkles,
} from "lucide-react";

import { getScheduleEvents, getScheduleFilters } from "../../../api/elearning/schedule/schedule";
import { hasPermission } from "../../../utils/permissionUtils";
import { getCurrentUser } from "../../../utils/auth";
import { startScheduleDashboardTour } from "./scheduleDashboardTour";
import ScheduleFilter from "./ScheduleFilter";
import CalendarGrid from "./components/CalendarGrid";
import MobileAgenda from "./components/MobileAgenda";
import EventDetailModal from "./components/EventDetailModal";
import MonthView from "./components/MonthView";
import { exportSchedulePdf } from "./exportSchedulePdf";

const SESSION_SUPER_ROLES = [
  "super admin",
  "superadmin",
  "administrator",
  "system administrator",
];

function isSuperAdminSession() {
  try {
    const u = getCurrentUser() || {};
    const roles = Array.isArray(u.roles) ? u.roles : [];
    return roles
      .map((r) => String(r || "").trim().toLowerCase())
      .some((r) => SESSION_SUPER_ROLES.includes(r));
  } catch {
    return false;
  }
}

function readUserBranchId() {
  try {
    const u = getCurrentUser() || {};
    return u.branch?.id || u.branch_id || u.branchId || null;
  } catch {
    return null;
  }
}

function getScheduleDateRange(currentDate, viewMode, isMobile) {
  if (isMobile) {
    const d = dayjs(currentDate).format("YYYY-MM-DD");
    return { from: d, to: d };
  }
  if (viewMode === "month") {
    return {
      from: dayjs(currentDate).startOf("month").format("YYYY-MM-DD"),
      to: dayjs(currentDate).endOf("month").format("YYYY-MM-DD"),
    };
  }
  if (viewMode === "week") {
    return {
      from: dayjs(currentDate).startOf("isoWeek").format("YYYY-MM-DD"),
      to: dayjs(currentDate).endOf("isoWeek").format("YYYY-MM-DD"),
    };
  }
  const d = dayjs(currentDate).format("YYYY-MM-DD");
  return { from: d, to: d };
}

const ScheduleDashboard = () => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const canQuickTour = hasPermission("elearning.calendar", "R");

  const handleQuickTour = useCallback(() => {
    window.setTimeout(() => startScheduleDashboardTour(), 80);
  }, []);

  // States
  const [viewMode, setViewMode] = useState("week"); // month | week | day
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const dateInputRef = useRef(null);
  const isSuperAdmin = useMemo(() => isSuperAdminSession(), []);
  const [filters, setFilters] = useState(() => {
    const userBranchId = readUserBranchId();
    return {
      branch_id: isSuperAdminSession() ? "all" : userBranchId || "all",
      subject_id: "all",
      course_id: "all",
      search: "",
      venue: "center",
    };
  });
  const [events, setEvents] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    branches: [],
    subjects: [],
    courses: [],
    can_filter_all_branches: isSuperAdmin,
    default_branch_id: readUserBranchId(),
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getScheduleFilters()
      .then((data) =>
        setFilterOptions({
          branches: data.branches || [],
          subjects: data.subjects || [],
          courses: data.courses || [],
          can_filter_all_branches: Boolean(data.can_filter_all_branches),
          default_branch_id: data.default_branch_id || null,
        })
      )
      .catch(() =>
        setFilterOptions({
          branches: [],
          subjects: [],
          courses: [],
          can_filter_all_branches: isSuperAdmin,
          default_branch_id: readUserBranchId(),
        })
      );
  }, [isSuperAdmin]);

  useEffect(() => {
    if (filterOptions.can_filter_all_branches) return;
    const bid = filterOptions.default_branch_id || readUserBranchId();
    if (!bid) return;
    setFilters((prev) =>
      String(prev.branch_id) !== String(bid) ? { ...prev, branch_id: bid } : prev
    );
  }, [filterOptions.can_filter_all_branches, filterOptions.default_branch_id]);

  const fetchEvents = useCallback(() => {
    setLoading(true);
    setError(null);
    const { from, to } = getScheduleDateRange(currentDate, viewMode, isMobile);
    getScheduleEvents({
      branch_id: filters.branch_id,
      course_id: filters.course_id,
      subject_id: filters.subject_id,
      from,
      to,
      venue: filters.venue,
    })
      .then((data) => setEvents(data.events || []))
      .catch((err) => {
        console.error("Lỗi lấy thời khóa biểu:", err);
        setError("Không thể tải thời khóa biểu.");
        setEvents([]);
      })
      .finally(() => setLoading(false));
  }, [
    currentDate,
    viewMode,
    isMobile,
    filters.branch_id,
    filters.course_id,
    filters.subject_id,
    filters.venue,
  ]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Chế độ xem (Options)
  const viewOptions = [
    { id: "month", label: "Tháng", icon: LayoutGrid },
    { id: "week", label: "Tuần", icon: CalendarDays },
    { id: "day", label: "Ngày", icon: List },
  ];

  // Lọc theo ô tìm kiếm (branch/subject/course đã lọc ở API)
  const filteredData = useMemo(() => {
    if (!filters.search.trim()) return events;
    const q = filters.search.toLowerCase();
    return events.filter(
      (ev) =>
        (ev.class && ev.class.toLowerCase().includes(q)) ||
        (ev.teacher && ev.teacher.toLowerCase().includes(q)) ||
        (ev.course && ev.course.toLowerCase().includes(q)) ||
        (ev.room && ev.room.toLowerCase().includes(q))
    );
  }, [events, filters.search]);

  // Điều hướng thời gian dựa trên viewMode
  const navigateDate = (direction) => {
    if (isMobile) {
      // Mobile luôn nhảy theo ngày
      setCurrentDate(
        direction === "next"
          ? currentDate.add(1, "day")
          : currentDate.subtract(1, "day")
      );
      return;
    }

    // Desktop nhảy theo viewMode (Tuần/Tháng/Ngày)
    const unit =
      viewMode === "week" ? "week" : viewMode === "month" ? "month" : "day";
    setCurrentDate(
      direction === "next"
        ? currentDate.add(1, unit)
        : currentDate.subtract(1, unit)
    );
  };

  const handleLabelClick = () => {
    // Kích hoạt bảng chọn ngày của trình duyệt một cách trực tiếp
    if (dateInputRef.current) {
      if (typeof dateInputRef.current.showPicker === "function") {
        dateInputRef.current.showPicker();
      } else {
        // Fallback cho trình duyệt cũ
        dateInputRef.current.focus();
      }
    }
  };

  const getTimeLabel = () => {
    // Nếu là Mobile, luôn hiển thị ngày chính xác đang chọn
    if (isMobile) {
      return currentDate.format("DD/MM/YYYY");
    }

    // Các logic cũ cho Desktop giữ nguyên
    if (viewMode === "month") {
      // Bọc [THÁNG] để dayjs giữ nguyên chuỗi này
      return currentDate.format("[THÁNG] MM, YYYY");
    }

    if (viewMode === "week") {
      const start = currentDate.startOf("isoWeek");
      const end = currentDate.endOf("isoWeek");
      return `${start.format("DD/MM")} - ${end.format("DD/MM/YYYY")}`;
    }
    return currentDate.format("DD/MM/YYYY");
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      await exportSchedulePdf({
        events: filteredData,
        filters,
        filterOptions,
        periodLabel: getTimeLabel(),
        viewMode: isMobile ? "day" : viewMode,
      });
    } catch (error) {
      console.error("Lỗi xuất PDF:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleEventClick = useCallback(
    (ev, dateOverride) => {
      let sessionDate;
      if (dateOverride) {
        sessionDate = dayjs(dateOverride).format("YYYY-MM-DD");
      } else if (isMobile || viewMode === "day") {
        sessionDate = currentDate.format("YYYY-MM-DD");
      } else if (viewMode === "week") {
        sessionDate = currentDate
          .startOf("isoWeek")
          .add((ev?.day || 1) - 1, "day")
          .format("YYYY-MM-DD");
      } else {
        sessionDate = currentDate.format("YYYY-MM-DD");
      }
      setSelectedEvent({ ...ev, session_date: sessionDate });
    },
    [currentDate, isMobile, viewMode]
  );

  return (
    <div className="min-h-screen w-full bg-[#F8FAFC] font-sans">
      {/* HEADER SECTION */}
      <header className="w-full bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
        <div className="w-full px-4 md:px-6 lg:px-8 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3" data-tour="schedule-header">
            <div className="bg-red-700 p-2 rounded-xl text-white shadow-lg shadow-red-200">
              <CalendarIcon size={24} />
            </div>
            <div>
              <h1 className="text-xl font-black text-gray-800 tracking-tight uppercase">
                Quản lý <span className="text-red-700">Thời khóa biểu</span>
              </h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                <p className="text-[11px] text-gray-500 font-bold uppercase tracking-widest text-nowrap">
                  Hệ thống báo giảng trực tuyến
                </p>
              </div>
            </div>
          </div>

          {/* VIEW SWITCHER & NAVIGATION */}
          <div className="flex flex-wrap items-center gap-3" data-tour="schedule-top-nav">
            {canQuickTour ? (
              <button
                type="button"
                onClick={handleQuickTour}
                title="Hướng dẫn nhanh"
                aria-label="Hướng dẫn nhanh"
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-red-200 bg-red-50/90 text-red-800 shadow-sm transition hover:border-red-300 hover:bg-red-100 md:h-auto md:w-auto md:gap-2 md:px-3 md:py-2.5 md:text-xs md:font-bold md:uppercase md:tracking-wide"
              >
                <Sparkles className="h-5 w-5 text-red-600 md:h-4 md:w-4" strokeWidth={2.25} aria-hidden />
                <span className="hidden md:inline">Hướng dẫn nhanh</span>
              </button>
            ) : null}
            {/* View Modes Selection - Chỉ hiển thị trên Desktop */}
            {!isMobile && (
              <div className="bg-gray-100 p-1 rounded-2xl flex items-center">
                {viewOptions.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setViewMode(opt.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                      viewMode === opt.id
                        ? "bg-white text-red-700 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <opt.icon size={14} />
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Date Navigator Controls */}
            <div className="flex items-center bg-white border border-gray-200 rounded-2xl p-1 shadow-sm">
              <button
                onClick={() => navigateDate("prev")}
                className="p-2 hover:bg-gray-50 rounded-xl text-gray-500 transition-colors"
              >
                <ChevronLeft size={18} />
              </button>

              {/* PHẦN SỬA ĐỔI CHÍNH Ở ĐÂY */}
              <div
                className="relative flex items-center group cursor-pointer"
                onClick={handleLabelClick}
              >
                {/* Input hoàn toàn ẩn và không chiếm diện tích nhưng vẫn nhận event */}
                <input
                  ref={dateInputRef}
                  type={viewMode === "month" ? "month" : "date"}
                  className="absolute invisible w-0 h-0"
                  value={currentDate.format(
                    viewMode === "month" ? "YYYY-MM" : "YYYY-MM-DD"
                  )}
                  onChange={(e) => {
                    if (e.target.value) setCurrentDate(dayjs(e.target.value));
                  }}
                />
                <span className="px-3 text-xs font-extrabold text-gray-700 min-w-[140px] text-center uppercase tracking-tighter group-hover:text-red-600 transition-colors flex items-center justify-center gap-1">
                  {getTimeLabel()}
                  <ChevronDown
                    size={12}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </span>
              </div>

              <button
                onClick={() => navigateDate("next")}
                className="p-2 hover:bg-gray-50 rounded-xl text-gray-500 transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <button
              onClick={() => setCurrentDate(dayjs())}
              className="px-4 py-2.5 bg-gray-800 text-white rounded-2xl text-xs font-bold hover:bg-black transition-all shadow-md active:scale-95"
            >
              HÔM NAY
            </button>
          </div>
        </div>
      </header>

      <main className="w-full max-w-none px-4 md:px-6 lg:px-8 py-6">
        {/* 1. FILTER BAR */}
        <ScheduleFilter
          filters={filters}
          setFilters={setFilters}
          filterOptions={filterOptions}
          canFilterAllBranches={filterOptions.can_filter_all_branches}
          onRefresh={fetchEvents}
          onExportPDF={handleExportPDF}
          isExporting={isExporting}
          viewMode={viewMode}
        />

        {/* 2. DASHBOARD CONTENT - Có ID để xuất PDF */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6"
          data-tour="schedule-content-area"
        >
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-sm font-medium">
              {error}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full" />
            </div>
          ) : isMobile || viewMode === "day" ? (
            /* Trên Mobile (mặc định) hoặc Desktop chọn Day: Dùng Agenda List */
            <div
              id="schedule-content"
              className="w-full bg-white p-4 rounded-[2rem]"
            >
              <MobileAgenda
                events={filteredData}
                currentDate={currentDate}
                setCurrentDate={setCurrentDate}
                onEventClick={handleEventClick}
              />
            </div>
          ) : (
            /* Chế độ xem Desktop: Tuần và Tháng */
            <div
              id="schedule-content"
              className="bg-white p-6 rounded-[2rem] shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden"
            >
              {/* Header bổ sung chỉ xuất hiện trong PDF (ẩn trên giao diện web) */}
              <div className="hidden pdf-only mb-6 border-b-2 border-red-700 pb-4">
                <h2 className="text-2xl font-bold text-gray-800 uppercase">
                  {viewMode === "week"
                    ? "Lịch Dạy Theo Tuần"
                    : "Lịch Dạy Theo Tháng"}
                </h2>
                <p className="text-gray-600 font-medium">
                  Địa điểm:{" "}
                  {filters.venue === "school"
                    ? "Tại trường"
                    : filters.venue === "all"
                      ? "Tất cả"
                      : "Trung tâm"}{" "}
                  | Cơ sở:{" "}
                  {filters.branch_id === "all"
                    ? "Tất cả chi nhánh"
                    : filterOptions.branches?.find((b) => b.id === filters.branch_id)?.name || filters.branch_id}{" "}
                  | {getTimeLabel()}
                </p>
              </div>

              {viewMode === "week" ? (
                <CalendarGrid
                  events={filteredData}
                  currentDate={currentDate}
                  onEventClick={handleEventClick}
                />
              ) : (
                <MonthView
                  events={filteredData}
                  currentDate={currentDate}
                  onEventClick={handleEventClick}
                />
              )}
            </div>
          )}
        </motion.div>
      </main>

      {/* Nút hành động nổi cho bản Mobile */}
      {isMobile && (
        <button className="fixed bottom-6 right-6 w-14 h-14 bg-red-700 text-white rounded-full shadow-2xl flex items-center justify-center active:scale-90 transition-all z-50">
          <Plus size={28} />
        </button>
      )}

      {/* POPUP CHI TIẾT SỰ KIỆN / BÁO GIẢNG */}
      <AnimatePresence>
        {selectedEvent && (
          <EventDetailModal
            event={selectedEvent}
            sessionDate={selectedEvent.session_date}
            onClose={() => setSelectedEvent(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ScheduleDashboard;
